import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import '../styles/klink-details.css';

interface KlinkFormData {
  klink: string;
  url: string;
  owner?: string;
}

interface SimilarKlink {
  klink: string;
  url: string;
  uses: number;
  owner: string;
}

const KlinkDetailsPage: React.FC = () => {
  const { klink: klinkParam } = useParams<{ klink: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { apiFetch } = useAuth();
  const isEditMode = !!klinkParam;
  const urlInputRef = useRef<HTMLInputElement>(null);

  // Get new_klink from URL parameters
  const searchParams = new URLSearchParams(location.search);
  const newKlink = searchParams.get('new_klink') || '';

  const [formData, setFormData] = useState<KlinkFormData>({
    klink: klinkParam || newKlink,
    url: '',
    owner: '',
  });
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(isEditMode);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [similarKlinks, setSimilarKlinks] = useState<SimilarKlink[]>([]);
  const [isLoadingSimilar, setIsLoadingSimilar] = useState(false);

  // Set focus on URL input when in create mode with new_klink
  useEffect(() => {
    if (!isEditMode && newKlink && urlInputRef.current) {
      urlInputRef.current.focus();
    }
  }, [isEditMode, newKlink]);

  // Fetch similar klinks when in create mode and klink value changes
  const fetchSimilarKlinks = async (searchTerm: string) => {
    if (isEditMode || !searchTerm.trim()) {
      setSimilarKlinks([]);
      return;
    }

    try {
      setIsLoadingSimilar(true);
      const response = await apiFetch(`${process.env.REACT_APP_API_BASE_URL}/api/klinks?search=${encodeURIComponent(searchTerm)}`);
      const data = await response.json();
      
      // Filter results to only include entries where klink name matches the search term
      const filteredResults = (data || []).filter((k: SimilarKlink) => 
        k.klink.toLowerCase().includes(searchTerm.toLowerCase())
      );
      
      // Limit to max 10 results
      setSimilarKlinks(filteredResults.slice(0, 10));
    } catch (err: any) {
      console.error('Failed to fetch similar klinks:', err);
      setSimilarKlinks([]);
    } finally {
      setIsLoadingSimilar(false);
    }
  };

  // Debounced search for similar klinks
  useEffect(() => {
    if (!isEditMode && formData.klink) {
      const timeoutId = setTimeout(() => {
        fetchSimilarKlinks(formData.klink);
      }, 500);
      return () => clearTimeout(timeoutId);
    }
  }, [formData.klink, isEditMode, apiFetch]);

  useEffect(() => {
    const fetchKlink = async () => {
      if (!isEditMode) return;

      try {
        setIsLoading(true);
        setError(null);
        const response = await apiFetch(`${process.env.REACT_APP_API_BASE_URL}/api/klinks/${klinkParam}`);
        const data = await response.json();
        setFormData({
          klink: data.klink,
          url: data.url,
          owner: data.owner,
        });
      } catch (err: any) {
        setError(err.message || 'Failed to fetch klink details');
      } finally {
        setIsLoading(false);
      }
    };

    fetchKlink();
  }, [klinkParam, isEditMode, apiFetch]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      const url = isEditMode
        ? `${process.env.REACT_APP_API_BASE_URL}/api/klinks/${klinkParam}`
        : `${process.env.REACT_APP_API_BASE_URL}/api/klinks`;

      const method = isEditMode ? 'PUT' : 'POST';
      const body = isEditMode ? { url: formData.url } : formData;

      const response = await apiFetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to save klink');
      }

      navigate('/klinks/list');
    } catch (err: any) {
      setError(err.message || 'An error occurred while saving the klink');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!isEditMode || !window.confirm('Are you sure you want to delete this klink?')) {
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const response = await apiFetch(`${process.env.REACT_APP_API_BASE_URL}/api/klinks/${klinkParam}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete klink');
      }

      navigate('/klinks/list');
    } catch (err: any) {
      setError(err.message || 'An error occurred while deleting the klink');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="loading-container">
        Loading klink details...
      </div>
    );
  }

  return (
    <div className="details-container">
      <h1 className="page-title">{isEditMode ? 'Edit k-link' : 'Create new k-link'}</h1>
      
      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label className="form-label">k-link</label>
          <input
            type="text"
            id="klink"
            value={formData.klink}
            onChange={(e) => setFormData({ ...formData, klink: e.target.value })}
            disabled={isEditMode}
            required
            className="form-input"
          />
        </div>

        <div className="form-group">
          <label className="form-label">URL</label>
          <input
            ref={urlInputRef}
            type="text"
            id="url"
            value={formData.url}
            onChange={(e) => setFormData({ ...formData, url: e.target.value })}
            required
            className="form-input"
          />
        </div>

        {isEditMode && (
          <div className="form-group">
            <label className="form-label">Owner</label>
            <input
              type="text"
              id="owner"
              value={formData.owner}
              disabled
              className="form-input"
            />
          </div>
        )}

        <div className="button-group">
          <button
            type="submit"
            disabled={isSubmitting}
            className="submit-button"
          >
            {isSubmitting ? 'Saving...' : isEditMode ? 'Update' : 'Create'}
          </button>
          
          {isEditMode && (
            <button
              type="button"
              onClick={handleDelete}
              disabled={isSubmitting}
              className="delete-button"
            >
              Delete
            </button>
          )}
        </div>
      </form>

      {/* Similar klinks table - only show in create mode */}
      {!isEditMode && (similarKlinks.length > 0 || isLoadingSimilar) && (
        <div className="similar-klinks-section">
          <h2 className="similar-klinks-title">Similar existing k-links</h2>
          {isLoadingSimilar ? (
            <div className="loading-container">
              Searching for similar k-links...
            </div>
          ) : (
            <div className="card table-container">
              <table>
                <thead>
                  <tr>
                    <th>k-link</th>
                    <th>URL</th>
                  </tr>
                </thead>
                <tbody>
                  {similarKlinks.map((k) => (
                    <tr key={k.klink}>
                      <td>
                        <a 
                          href={`/klinks/${k.klink}`}
                          className="klink-link"
                        >
                          {k.klink}
                        </a>
                      </td>
                      <td className="url-cell">
                        <a 
                          href={k.url.startsWith('http') ? k.url : `https://${k.url}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="url-link"
                        >
                          {k.url}
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default KlinkDetailsPage;
